import FormInput from "@components/BuyForm/FormInput";

import { useNavigation } from "@contexts/NavigationContainer";
import {
	addOpacityToColor,
	buyFlowTextStyles,
	colours,
	mediaQuery,
	useDeviceDetect,
} from "@helpers";
import { ActivateFlowStep } from "@helpers/ActivateFlowStep";
import { Actioner, BuyFlowActionKeys, GTMActionKeys } from "@services/actioner";
import { defaultFelixClientConfig } from "@services/sdk/create-client";
import { fetchWithTimeout } from "@utils/fetchWithTimeout";
import Image from "next/image";
import React, { useState } from "react";
import styled from "styled-components";

import Button from "@components/BuyForm/Button";
import {
	FRAME_PADDING_HORIZONTAL_MOBILE,
	FRAME_PADDING_HORIZONTAL_TABLET,
	FRAME_PADDING_VERTICAL_MOBILE,
	FRAME_PADDING_VERTICAL_TABLET,
	FrameWithoutPadding,
} from "@components/BuyForm/Frame";
import { IconContainer } from "@components/BuyForm/HeroIconPageLayout";
import BuyFlowIcon from "@components/BuyForm/Icon";
import Icon from "@components/Icon";
import ModalOverlay from "@components/ModalOverlay";
import { pxToRem } from "@helpers/buyFlowTextStyles";
import { mixpanelTrack } from "@helpers/mixpanel.helper";
import {
	msisdnAtom,
	retailBlockedAtom,
	simAtom,
	simChannelAtom,
	simProductIdAtom,
} from "@contextsactivateAtoms";
import { simTypeAtom } from "@contextsbuyAtoms";
import { useAtom } from "jotai";

const ModalIcon = styled(BuyFlowIcon)`
  transform: scale(1.5);

  ${mediaQuery.tabletAndUp} {
    transform: scale(4);
  }
`;

const ModalBody = styled.div`
  ul {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  span {
    font-weight: bold;
  }

  ${buyFlowTextStyles.LARGE}
  line-height: 24px !important;
  font-size: 16px !important;

  p {
    ${buyFlowTextStyles.LARGE}
  }

  p br:first-child {
    display: none;
  }

  margin-bottom: 1rem;
`;

const ModalTitle = styled.h1`
  text-align: center;
  font-size: 24px !important;
  margin: 12px 0;
`;

const ModalContentWrapper = styled.div`
  gap: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  #confirm-button {
    media (max-width: 767px) {
      width: 100%;
    }
  }
`;

const FrameFooter = styled.div`
  background-color: ${colours.LIGHT_GREY};
  padding: ${pxToRem(20)} ${FRAME_PADDING_HORIZONTAL_MOBILE};

  ${mediaQuery.tabletAndUp} {
    padding: 1.25rem ${FRAME_PADDING_HORIZONTAL_TABLET} 1.25rem;
  }
`;

const ContinueFooter = styled(FrameFooter)`
  display: flex;
  flex-direction: column;
  padding: 1.5rem ${FRAME_PADDING_HORIZONTAL_MOBILE};
  #confirm-button {
    width: 100%;
    max-width: 300px;
    margin: auto;
    &:hover {
      color: #ffffff !important;
    }
  }

  ${mediaQuery.tabletAndUp} {
    flex-direction: row-reverse;
    padding: 1.75rem ${FRAME_PADDING_HORIZONTAL_TABLET};
  }
`;

const FrameContent = styled.div`
  padding: ${FRAME_PADDING_VERTICAL_MOBILE} ${FRAME_PADDING_HORIZONTAL_MOBILE}
    2.25rem;

  & + & {
    border-top: 1px solid ${addOpacityToColor(colours.MIDNIGHT_BLUE, 0.1)};
  }

  ${mediaQuery.tabletAndUp} {
    padding: ${FRAME_PADDING_VERTICAL_TABLET} ${FRAME_PADDING_HORIZONTAL_TABLET}
      2.25rem;
  }
  h2 {
    font-size: 1.25rem;
  }
`;

const BarcodeNumber = styled.div`
  background: #ebfcf6;
  border-radius: 15px;
  width: 135px;
  height: 27px;
  text-align: center;
  font-size: 12px;
  padding: 6px 0px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  span {
    font-size: 12px;
  }
  img {
    content: "\\e910";
    font-family: icons;
    font-size: 14px;
    color: white;
    background-color: #3b855b;
    width: 30px;
    height: 30px;
    border-radius: 15px;
    margin-right: 10px;
  }
`;

const SimExampleWrapper = styled.div`
  display: flex;
  align-content: center;
  justify-content: center;
  width: 100%;
  margin: 20px 0px;
`;

const SimExampleContainer = styled.div`
  height: 100px;
  width: 100px;
`;

const useBarcodeInput = (initialValue) => {
	const [value, setValue] = useState(initialValue);
	const [error, setError] = useState("");
	const [warning, setWarning] = useState("");

	const validate = () => {};

	const onChange = (e) => {
		const barcode = e.target.value.toLowerCase().replace(/\D/g, "");
		if (barcode.length > 20) {
			return;
		}

		setValue(barcode);
	};

	const onFocus = () => {
		setError("");
		setWarning("");
	};

	const onBlur = () => {
		if (!value) return;
		validate();
	};

	return {
		error,
		onBlur,
		onChange,
		onFocus,
		setError,
		validate,
		value,
		warning,
	};
};

const RegisterSim = () => {
	const { isAndroid } = useDeviceDetect();

	const [, setSim] = useAtom(simAtom);
	const [, setSimType] = useAtom(simTypeAtom);
	const [, setSimProductId] = useAtom(simProductIdAtom);
	const [, setRetailBlocked] = useAtom(retailBlockedAtom);
	const [, setMsisdn] = useAtom(msisdnAtom);
	const [simChannel, setSimChannel] = useAtom(simChannelAtom);

	const [isLoading, setIsLoading] = useState(false);

	const [simErrorModalContent, setSimErrorModalContent] = useState(undefined);

	const { navigate, routeName } = useNavigation();

	const SIMBarcode = useBarcodeInput("896103", routeName);

	const handleSIMStatus = async (response) => {
		const body = await response.json();

		switch (response.status) {
			case 200: {
				if (body.data.retailBlocked && body.data.simChannel === "RETAIL") {
					setSimErrorModalContent({
						title: "SIM card is still locked",
						body: (
							<>
								<span>SIM cards need to be unlocked</span> at your point of
								sale. Please take your SIM card and receipt back to the store to
								unlock it before trying again.
							</>
						),
						closeButton: "Okay",
						iconName: "lock",
						showAppBadge: false,
					});

					return false;
				}

				if (body.data.simChannel === "NON_RETAIL") {
					setSimErrorModalContent({
						title: "Activate your SIM using our mobile app",
						body: (
							<ul>
								<li>
									<span>Your SIM was purchased online</span> and needs to be
									activated using our mobile app.
								</li>
								<li>
									<span>1. Download our app</span> from the app store and log in
									using your email address and password.
								</li>
								<li>
									<span>2. Use our app</span> to activate your SIM.
								</li>
							</ul>
						),
						iconName: "mobile",
						showAppBadge: true,
					});

					return false;
				}

				setSim(body.data?.sim);
				setSimType(body.data?.simType);
				setSimProductId(body.data?.simProductId);
				setRetailBlocked(body.data?.retailBlocked);
				setSimChannel(body.data?.simChannel);
				setMsisdn(body.data?.msisdn);

				navigate(ActivateFlowStep.ConfirmOrUpdatePlan);
				return true;
			}

			case 400:
				setSimErrorModalContent({
					title: "Incorrect SIM number",
					body: (
						<>
							The SIM you entered was incorrect for a felix SIM please check the
							number <span>{SIMBarcode.value}</span> against the number on your
							felix SIM card and try again.
						</>
					),
					closeButton: "Retry",
					iconName: "close",
					showAppBadge: false,
				});
				Actioner.trigger(GTMActionKeys.FORM_ERROR, {
					formName: "web_activate_retail",
					formStepName: routeName,
					formErrorDetails: `{The SIM you entered was incorrect for a felix SIM please check the
            number ${SIMBarcode.value} against the number on your
            felix SIM card and try again.}`,
				});

				return false;
			case 404:
				setSimErrorModalContent({
					title: "Incorrect SIM number",
					body: (
						<>
							The SIM you entered was incorrect for a felix SIM please check the
							number <span>{SIMBarcode.value}</span> against the number on your
							felix SIM card and try again.
						</>
					),
					closeButton: "Retry",
					iconName: "close",
					showAppBadge: false,
				});
				Actioner.trigger(GTMActionKeys.FORM_ERROR, {
					formName: "web_activate_retail",
					formStepName: routeName,
					formErrorDetails: `{The SIM you entered was incorrect for a felix SIM please check the
            number ${SIMBarcode.value} against the number on your
            felix SIM card and try again.}`,
				});

				return false;

			default:
				setSimErrorModalContent({
					title: "We couldn't check your SIM",
					body: "Sorry, there was an error connecting to the server. Please try again.",
					closeButton: "Retry",
					iconName: "close",
					showAppBadge: false,
				});
				return false;
		}
	};

	const handleRegister = async () => {
		setIsLoading(true);

		const params = {
			sim: SIMBarcode.value,
		};
		await fetchWithTimeout(
			`${defaultFelixClientConfig.baseURL}sim/query?${new URLSearchParams(params).toString()}`,
			{
				method: "GET",
				throwOnHttpError: false,
				headers: {
					Authorization: process.env.NEXT_PUBLIC_MRTM_PUBLIC_ENDPOINTS_KEY,
					"Content-Type": "application/json",
				},
			},
		)
			.then(async (data) => {
				const isConfirmed = await handleSIMStatus(data);
				if (!isConfirmed) return;

				Actioner.trigger(BuyFlowActionKeys.RETAIL_SIM_CONFIRMED);
				Actioner.trigger(GTMActionKeys.FORM_STEP_COMPLETE, {
					formName: "web_activate_retail",
					formStepName: routeName,
				});
				setSimChannel("RETAIL");
				Actioner.trigger(BuyFlowActionKeys.BUY_SET_SIM_CHANNEL_EVENT, {
					simChannel,
				});
			})
			.catch((error) => {
				console.error("Unable to query SIM", error);
				setSimErrorModalContent({
					title: "We couldn't check your SIM",
					body: "Sorry, there was an error connecting to the server. Please try again.",
					closeButton: "Retry",
					iconName: "close",
					showAppBadge: false,
				});
			})
			.finally(() => {
				setIsLoading(false);
			});
	};
	const appStoreUrl = "https://flx.mobi/2ixmDsqqiob";
	const googlePlayUrl = "https://flx.mobi/5YIlJpvqiob";
	const iconSizes = {
		iconMobile: 14,
		iconTablet: 14,
		iconLaptop: 14,
		iconDesktop: 14,
		iconDesktopLarge: 14,
	};
	return (
		<FrameWithoutPadding>
			<FrameContent>
				<h2> Find the SIM number under the barcode on your felix SIM</h2>

				<SimExampleWrapper>
					<SimExampleContainer>
						<Image
							src={"/images/sim-example.png"}
							alt="SIM card example"
							width={100}
							height={114}
						/>
					</SimExampleContainer>
				</SimExampleWrapper>

				<FormInput
					label="SIM number"
					id="Barcode"
					onChange={SIMBarcode.onChange}
					onFocus={SIMBarcode.onFocus}
					onBlur={SIMBarcode.onBlur}
					value={SIMBarcode.value || ""}
					feedback={SIMBarcode.error || SIMBarcode.warning}
					feedbackType={SIMBarcode.warning ? "warning" : "error"}
					autoComplete="on"
					autoFocus
				/>
				{SIMBarcode.value.length === 20 && (
					<>
						<BarcodeNumber>
							<Icon
								iconName="tick-white"
								iconSizes={iconSizes}
								alt="white tick icon"
							/>

							<span>20 digit number</span>
						</BarcodeNumber>
						<p style={{ marginTop: "24px" }}>
							Double check your number before submitting
						</p>
					</>
				)}
			</FrameContent>
			<ContinueFooter>
				<Button
					id="confirm-button"
					onClick={handleRegister}
					disabled={isLoading}
				>
					Register Your SIM
				</Button>
			</ContinueFooter>

			<ModalOverlay
				show={!!simErrorModalContent}
				noMaxWidth
				onClose={() => setSimErrorModalContent(undefined)}
			>
				<ModalContentWrapper>
					{!!simErrorModalContent?.iconName && (
						<IconContainer shadowVisible>
							<ModalIcon iconName={simErrorModalContent?.iconName} />
						</IconContainer>
					)}
					<ModalTitle style={{ marginBottom: "8px" }}>
						{simErrorModalContent?.title}
					</ModalTitle>
					<ModalBody>{simErrorModalContent?.body}</ModalBody>
					{!!simErrorModalContent?.closeButton && (
						<Button
							id="confirm-button"
							onClick={() => setSimErrorModalContent(undefined)}
						>
							{simErrorModalContent?.closeButton}
						</Button>
					)}
					{simErrorModalContent?.showAppBadge &&
						(isAndroid ? (
							<a
								onClick={() => {
									setSimErrorModalContent(undefined);
									mixpanelTrack("download_app_clicked", {
										cta_position: "Retail-register-sim",
										cta_text: "androidUrl",
									});
								}}
								href={googlePlayUrl}
								target="_blank"
								rel="noreferrer"
							>
								<Image
									alt="Get it on Google Play"
									src={"/images/PlayStoreBadge.png"}
									width={162.5}
									height={48}
									data-gtm-cta-text="androidUrl"
									data-gtm-cta-position="Retail-register-sim"
								/>
							</a>
						) : (
							<a
								onClick={() => {
									setSimErrorModalContent(undefined);
									mixpanelTrack("download_app_clicked", {
										cta_position: "Retail-register-sim",
										cta_text: "appleUrl",
									});
								}}
								href={appStoreUrl}
								target="_blank"
								rel="noreferrer"
							>
								<Image
									alt="Get it on Appstore"
									src={"/images/AppStoreBadge.png"}
									width={162.5}
									height={48}
									data-gtm-cta-text="appleUrl"
									data-gtm-cta-position="Retail-register-sim"
								/>
							</a>
						))}
				</ModalContentWrapper>
			</ModalOverlay>
		</FrameWithoutPadding>
	);
};

export default RegisterSim;
